import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRegister } from '@/hooks/useAuth';
import PasswordInput from '@/components/PasswordInput';
import ReCaptcha from '@/components/ReCaptcha';
import { PASSWORD_RULES, registerErrorMessage, registerFormError } from '@/lib/registerErrors';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const register = useRegister();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const formError = registerFormError({ name, email, phone, password, confirmPassword });
    if (formError) {
      setError(formError);
      return;
    }

    // Check if captcha is required (site key is configured) and completed
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    if (siteKey && !captchaToken) {
      setError('Confirma que não és um robô.');
      return;
    }

    register.mutate(
      { name, email, phone, password, captchaToken: captchaToken || undefined },
      {
        // Registration always goes into pending approval — show confirmation screen.
        onSuccess: () => setSubmitted(true),
        onError: () => {
          // Reset captcha on error
          recaptchaRef.current?.reset();
          setCaptchaToken(null);
        },
      }
    );
  };

  // ── Pending approval confirmation screen ──────────────────────────────────
  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Registo submetido | DIGGER</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="max-w-md w-full text-center space-y-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">Registo submetido!</h1>
            <p className="text-muted-foreground">
              A tua conta foi criada com sucesso e aguarda aprovação por parte de um administrador.
              Receberás acesso assim que o teu registo for aprovado.
            </p>
            <Link
              to="/login"
              className="inline-block btn-primary px-6 py-2"
            >
              Voltar ao login
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Criar conta | DIGGER</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-bold text-foreground">
              DIGGER
            </h1>
            <h2 className="mt-6 text-center text-2xl font-semibold text-foreground">
              Cria a tua conta
            </h2>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit} aria-busy={register.isPending}>
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {(error || register.isError) && (
                <div id="register-error" className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
                  {error || registerErrorMessage(register.error)}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="label">
                  Nome completo
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className="input mt-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-invalid={!!(error || register.isError) || undefined}
                  aria-describedby={(error || register.isError) ? 'register-error' : undefined}
                />
              </div>

              <div>
                <label htmlFor="email" className="label">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input mt-1"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!(error || register.isError) || undefined}
                  aria-describedby={(error || register.isError) ? 'register-error' : undefined}
                />
              </div>

              <div>
                <label htmlFor="phone" className="label">
                  Telemóvel
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  className="input mt-1"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  aria-invalid={!!(error || register.isError) || undefined}
                  aria-describedby={(error || register.isError) ? 'register-error' : undefined}
                />
              </div>

              <div>
                <label htmlFor="password" className="label">
                  Palavra-passe
                </label>
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!(error || register.isError) || undefined}
                  aria-describedby={`password-hint${(error || register.isError) ? ' register-error' : ''}`}
                />
                <p id="password-hint" className="mt-1 text-xs text-muted-foreground">
                  {PASSWORD_RULES}
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label">
                  Confirmar palavra-passe
                </label>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  revealLabel="confirmação da palavra-passe"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  aria-invalid={!!(error || register.isError) || undefined}
                  aria-describedby={(error || register.isError) ? 'register-error' : undefined}
                />
              </div>

              <ReCaptcha
                ref={recaptchaRef}
                onChange={handleCaptchaChange}
                onExpired={handleCaptchaExpired}
              />
            </div>

            <button
              type="submit"
              disabled={register.isPending}
              className="btn-primary w-full"
            >
              {register.isPending ? 'A criar conta…' : 'Criar conta'}
            </button>

            <p className="text-center text-sm text-muted-foreground">
              {'Já tens conta? '}
              <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                Entrar
              </Link>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}
